/**
 * QuickEntryGrid
 *
 * Editable AntD Table for manual bulk patient entry (up to 20 rows).
 *
 * Keyboard navigation:
 *   Tab / Shift+Tab  → next / previous cell
 *   Enter            → move down; on last data row → add new row
 *   Arrow keys       → native browser caret movement (no custom handling)
 *
 * Birth date: plain text input, accepts DD.MM.YYYY.
 *   gridRowToInput (PatientImport.tsx) normalises to YYYY-MM-DD before sending to the API.
 *
 * Paste (multi-cell from Excel / Google Sheets):
 *   Tab-separated columns, newline-separated rows.
 *   Pastes are applied starting at the currently focused cell.
 */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {Button, Input, Select, Table} from 'antd';
import {
    DeleteOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import {FormattedMessage, useIntl, type IntlShape} from 'react-intl';
import {RowStatusTag, RowStatus} from './RowStatusTag';
import {isValidPatientName} from '../../utils/nameValidator';

const MAX_ROWS = 20;

export interface GridRow {
    key: string;
    rowIndex: number;
    firstName: string;
    lastName: string;
    secondName: string;
    email: string;
    phone: string;
    birthDate: string;
    gender: string;
    nationalId: string;
    notes: string;
    // Set after server-side validation
    status?: RowStatus;
    validationErrors?: Record<string, string[]>;
    duplicateInfo?: Array<{type: string; name?: string; patientId?: string}>;
}

// Field order drives both Tab navigation and column rendering.
// birthDate is placed last so free-text fields (name / email / phone / notes)
// can be traversed without hitting the date-format requirement mid-row.
const EDITABLE_FIELDS: (keyof GridRow)[] = [
    'firstName',
    'lastName',
    'secondName',
    'email',
    'phone',
    'gender',
    'nationalId',
    'notes',
    'birthDate',
];

/**
 * Lightweight client-side field validator — runs on every keystroke.
 * Returns an array of translated error messages, or null if the value is acceptable.
 * Validation rules mirror the canonical PatientForm.tsx (name regex, phone E.164, email).
 * Keys must match what the server puts in validationErrors (camelCase).
 */
function clientValidateField(field: keyof GridRow, value: string, intl: IntlShape): string[] | null {
    const v = value.trim();
    if (!v) return null; // empty values: required-field check is done separately
    if (field === 'firstName' || field === 'lastName' || field === 'secondName') {
        if (v.length > 100) return [intl.formatMessage({id: 'field_too_long_message'})];
        if (!isValidPatientName(v)) return [intl.formatMessage({id: 'letters_only_field'})];
    }
    if (field === 'email') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return [intl.formatMessage({id: 'invalid_email'})];
    }
    if (field === 'phone') {
        const stripped = v.replace(/[\s-]/g, '');
        if (!/^\+[1-9]\d{1,14}$/.test(stripped)) return [intl.formatMessage({id: 'invalid_phone_e164'})];
    }
    return null;
}

function emptyRow(rowIndex: number): GridRow {
    return {
        key: `row-${rowIndex}-${Date.now()}`,
        rowIndex,
        firstName: '',
        lastName: '',
        secondName: '',
        email: '',
        phone: '',
        birthDate: '',
        gender: '',
        nationalId: '',
        notes: '',
    };
}

interface Props {
    rows: GridRow[];
    onChange: (rows: GridRow[]) => void;
    validating: boolean;
    country?: string;
}

interface FocusRef {
    rowKey: string;
    field: keyof GridRow;
}

export const QuickEntryGrid: React.FC<Props> = ({rows, onChange, validating, country}) => {
    const intl = useIntl();
    const focusRef = useRef<FocusRef | null>(null);
    // Map of "rowKey-field" → input ref
    const inputRefs = useRef<Record<string, any>>({});
    // Tracks whether each row's gender Select dropdown is currently open.
    // Used to prevent Enter from navigating away while the user is selecting an option.
    const genderOpenRef = useRef<Record<string, boolean>>({});

    const registerRef = useCallback(
        (rowKey: string, field: keyof GridRow) =>
            (el: any) => {
                inputRefs.current[`${rowKey}-${field}`] = el;
            },
        []
    );

    const focusCell = useCallback((rowKey: string, field: keyof GridRow) => {
        const el = inputRefs.current[`${rowKey}-${field}`];
        if (el) {
            el.focus();
            if (typeof el.select === 'function') el.select();
        }
        focusRef.current = {rowKey, field};
    }, []);

    // ---------- navigation helpers ----------

    const navigateCell = useCallback(
        (
            currentRowKey: string,
            currentField: keyof GridRow,
            direction: 'next' | 'prev' | 'down' | 'up' | 'nextRowFirst',
            currentRows: GridRow[],
            addRow: () => void
        ) => {
            const rowIdx = currentRows.findIndex(r => r.key === currentRowKey);
            const colIdx = EDITABLE_FIELDS.indexOf(currentField);

            if (direction === 'next') {
                if (colIdx < EDITABLE_FIELDS.length - 1) {
                    focusCell(currentRowKey, EDITABLE_FIELDS[colIdx + 1]);
                } else if (rowIdx < currentRows.length - 1) {
                    focusCell(currentRows[rowIdx + 1].key, EDITABLE_FIELDS[0]);
                } else if (currentRows.length < MAX_ROWS) {
                    addRow();
                }
            } else if (direction === 'prev') {
                if (colIdx > 0) {
                    focusCell(currentRowKey, EDITABLE_FIELDS[colIdx - 1]);
                } else if (rowIdx > 0) {
                    focusCell(
                        currentRows[rowIdx - 1].key,
                        EDITABLE_FIELDS[EDITABLE_FIELDS.length - 1]
                    );
                }
            } else if (direction === 'down') {
                if (rowIdx < currentRows.length - 1) {
                    focusCell(currentRows[rowIdx + 1].key, currentField);
                } else if (currentRows.length < MAX_ROWS) {
                    addRow();
                }
            } else if (direction === 'up') {
                if (rowIdx > 0) {
                    focusCell(currentRows[rowIdx - 1].key, currentField);
                }
            } else if (direction === 'nextRowFirst') {
                // Enter key: go to firstName of the next row (Excel-style new record).
                if (rowIdx < currentRows.length - 1) {
                    focusCell(currentRows[rowIdx + 1].key, EDITABLE_FIELDS[0]);
                } else if (currentRows.length < MAX_ROWS) {
                    addRow(); // addEmptyRow focuses firstName of the new row
                }
            }
        },
        [focusCell]
    );

    // ---------- row mutation helpers ----------

    const updateCell = useCallback(
        (rowKey: string, field: keyof GridRow, value: string) => {
            const requiredMsg = intl.formatMessage({id: 'required_field'});
            onChange(
                rows.map(r => {
                    if (r.key !== rowKey) return r;

                    const newErrors: Record<string, string[]> = {...(r.validationErrors ?? {})};

                    // Step 1: format check for the field being edited.
                    // This is the ONLY place that writes/clears errors for this field.
                    const fieldErrors = clientValidateField(field, value, intl);
                    if (fieldErrors?.length) {
                        newErrors[field as string] = fieldErrors;
                    } else {
                        delete newErrors[field as string];
                    }

                    // Step 2: required-field gate for firstName + lastName.
                    // Rule: only sets/clears the "required" placeholder.
                    // Never touches a format error written by Step 1 above.
                    const updated = {...r, [field]: value};
                    const hasAnyData = EDITABLE_FIELDS.some(f => (updated[f] as string)?.trim());

                    if (!hasAnyData) {
                        // Row is entirely blank — drop all errors.
                        return {...r, [field]: value, status: undefined, validationErrors: undefined};
                    }

                    (['firstName', 'lastName'] as const).forEach(req => {
                        const hasValue = (updated[req] as string)?.trim();
                        if (!hasValue) {
                            // Empty required field: set the required placeholder only when
                            // there is no format error already occupying this slot.
                            if (!newErrors[req]) {
                                newErrors[req] = [requiredMsg];
                            }
                        } else if (newErrors[req]?.[0] === requiredMsg) {
                            // Field now has a value and the only error was the required
                            // placeholder — clear it. Format errors survive untouched.
                            delete newErrors[req];
                        }
                    });

                    return {
                        ...r,
                        [field]: value,
                        status: undefined,
                        validationErrors: Object.keys(newErrors).length > 0 ? newErrors : undefined,
                    };
                })
            );
        },
        [rows, onChange, intl]
    );

    const addEmptyRow = useCallback(() => {
        if (rows.length >= MAX_ROWS) return;
        const newRow = emptyRow(rows.length);
        onChange([...rows, newRow]);
        // Focus happens after render in useEffect
        focusRef.current = {rowKey: newRow.key, field: 'firstName'};
    }, [rows, onChange]);

    const deleteRow = useCallback(
        (rowKey: string) => {
            onChange(rows.filter(r => r.key !== rowKey));
        },
        [rows, onChange]
    );

    // Focus the pending cell after rows change (e.g. after addRow)
    useEffect(() => {
        if (focusRef.current) {
            const {rowKey, field} = focusRef.current;
            const el = inputRefs.current[`${rowKey}-${field}`];
            if (el) {
                el.focus();
                focusRef.current = null;
            }
        }
    });

    // ---------- paste handler ----------

    const handlePaste = useCallback(
        (e: React.ClipboardEvent, rowKey: string, field: keyof GridRow) => {
            const text = e.clipboardData.getData('text/plain');
            if (!text.includes('\t') && !text.includes('\n')) return; // single cell, let default handle
            e.preventDefault();

            const pastedRows = text
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n')
                .trimEnd()
                .split('\n')
                .map(row => row.split('\t'));

            const startRowIdx = rows.findIndex(r => r.key === rowKey);
            const startColIdx = EDITABLE_FIELDS.indexOf(field);

            const updated = [...rows];

            pastedRows.forEach((pastedCols, deltaRow) => {
                const targetRowIdx = startRowIdx + deltaRow;

                // Auto-extend rows if needed
                while (updated.length <= targetRowIdx && updated.length < MAX_ROWS) {
                    updated.push(emptyRow(updated.length));
                }
                if (targetRowIdx >= MAX_ROWS) return;

                const targetRow = {...updated[targetRowIdx], status: undefined};
                pastedCols.forEach((cellVal, deltaCol) => {
                    const colIdx = startColIdx + deltaCol;
                    if (colIdx >= EDITABLE_FIELDS.length) return;
                    (targetRow as any)[EDITABLE_FIELDS[colIdx]] = cellVal.trim();
                });
                updated[targetRowIdx] = targetRow;
            });

            onChange(updated);
        },
        [rows, onChange]
    );

    // ---------- shared keyboard handler for all text inputs ----------

    // Keyboard handler for text inputs.
    // Owns Tab/Shift+Tab and Enter only.  All arrow keys are left entirely to the
    // browser — native caret movement in inputs, no grid row/column navigation.
    const makeKeyDownHandler = useCallback(
        (rowKey: string, field: keyof GridRow) =>
            (e: React.KeyboardEvent) => {
                if (e.key === 'Tab') {
                    e.preventDefault();
                    navigateCell(rowKey, field, e.shiftKey ? 'prev' : 'next', rows, addEmptyRow);
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    navigateCell(rowKey, field, 'nextRowFirst', rows, addEmptyRow);
                }
            },
        [navigateCell, rows, addEmptyRow]
    );

    // ---------- column definitions ----------

    const renderEditableCell = (
        field: keyof GridRow,
        row: GridRow,
        placeholder: string
    ) => {
        const hasError = row.validationErrors?.[field as string];
        // Error title: surfaced as native browser tooltip on hover (supplementary).
        // The Status column carries the always-visible summary.
        const errorTitle = hasError?.[0];

        if (field === 'gender') {
            return (
                <Select
                    ref={registerRef(row.key, field)}
                    size="small"
                    style={{width: '100%', minWidth: 80}}
                    value={(row[field] as string) || undefined}
                    allowClear
                    onChange={val => updateCell(row.key, field, val ?? '')}
                    onFocus={() => { focusRef.current = {rowKey: row.key, field}; }}
                    popupClassName="import-gender-select"
                    onDropdownVisibleChange={(open) => { genderOpenRef.current[row.key] = open; }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Tab') {
                            e.preventDefault();
                            navigateCell(row.key, field, e.shiftKey ? 'prev' : 'next', rows, addEmptyRow);
                        }
                        // Enter is handled entirely by AntD (open dropdown / select option).
                        // The user leaves Gender via Tab, not Enter.
                    }}
                    options={[
                        {value: 'male', label: intl.formatMessage({id: 'gender_male'})},
                        {value: 'female', label: intl.formatMessage({id: 'gender_female'})},
                    ]}
                />
            );
        }

        // Birth date: plain text input (DD.MM.YYYY).
        // PatientImport.gridRowToInput normalises to YYYY-MM-DD for the API.
        // Plain Input avoids all DatePicker keyboard/focus-trap issues.
        if (field === 'birthDate') {
            return (
                <Input
                    ref={registerRef(row.key, field)}
                    size="small"
                    style={{minWidth: 110}}
                    value={row.birthDate}
                    placeholder="DD.MM.YYYY"
                    // Layered autofill suppression — see generic Input below for rationale.
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    status={hasError ? 'error' : undefined}
                    title={errorTitle}
                    onChange={e => updateCell(row.key, field, e.target.value)}
                    onKeyDown={makeKeyDownHandler(row.key, field)}
                    onPaste={e => handlePaste(e, row.key, field)}
                    onFocus={() => { focusRef.current = {rowKey: row.key, field}; }}
                />
            );
        }

        return (
            // autoComplete:
            //   email field → "import-cell" (non-standard token; Chrome treats unknown
            //     tokens as "off" without the override it applies to the literal "off"
            //     value on personal-data fields; also avoids the password-manager popup
            //     that "new-password" triggers on email-looking inputs).
            //   all other fields → "new-password" (suppresses address/name autofill;
            //     Chrome never autofills saved addresses into password fields).
            //
            // autoCorrect / autoCapitalize / spellCheck: suppress iOS/macOS inline
            // corrections that interfere with data-entry speed in a spreadsheet grid.
            <Input
                ref={registerRef(row.key, field)}
                size="small"
                value={row[field] as string}
                placeholder={placeholder}
                autoComplete={field === 'email' ? 'import-cell' : 'new-password'}
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                status={hasError ? 'error' : undefined}
                title={errorTitle}
                onChange={e => updateCell(row.key, field, e.target.value)}
                onKeyDown={makeKeyDownHandler(row.key, field)}
                onPaste={e => handlePaste(e, row.key, field)}
                onFocus={() => {
                    focusRef.current = {rowKey: row.key, field};
                }}
            />
        );
    };

    const columns = useMemo(
        () => [
            {
                title: '#',
                width: 40,
                render: (_: any, __: GridRow, idx: number) => (
                    <span style={{color: '#999', fontSize: 12}}>{idx + 1}</span>
                ),
            },
            {
                title: <span><FormattedMessage id="first_name"/><span style={{color: '#ff4d4f', marginLeft: 2}}>*</span></span>,
                dataIndex: 'firstName',
                width: 130,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('firstName', row, intl.formatMessage({id: 'first_name'})),
            },
            {
                title: <span><FormattedMessage id="last_name"/><span style={{color: '#ff4d4f', marginLeft: 2}}>*</span></span>,
                dataIndex: 'lastName',
                width: 130,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('lastName', row, intl.formatMessage({id: 'last_name'})),
            },
            {
                title: <FormattedMessage id="surname"/>,
                dataIndex: 'secondName',
                width: 120,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('secondName', row, intl.formatMessage({id: 'surname'})),
            },
            {
                title: <FormattedMessage id="email"/>,
                dataIndex: 'email',
                width: 180,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('email', row, 'email@example.com'),
            },
            {
                title: <FormattedMessage id="phone"/>,
                dataIndex: 'phone',
                width: 160,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('phone', row, '+359888…'),
            },
            {
                title: <FormattedMessage id="gender"/>,
                dataIndex: 'gender',
                width: 90,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('gender', row, ''),
            },
            {
                title: country === 'BG'
                    ? <FormattedMessage id="egn"/>
                    : <FormattedMessage id="national_id_label"/>,
                dataIndex: 'nationalId',
                width: 120,
                render: (_: any, row: GridRow) =>
                    renderEditableCell(
                        'nationalId', row,
                        country === 'BG' ? intl.formatMessage({id: 'egn'}) : 'National ID'
                    ),
            },
            {
                title: <FormattedMessage id="notes"/>,
                dataIndex: 'notes',
                width: 160,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('notes', row, intl.formatMessage({id: 'notes'})),
            },
            {
                title: <FormattedMessage id="birth_date"/>,
                dataIndex: 'birthDate',
                width: 130,
                render: (_: any, row: GridRow) =>
                    renderEditableCell('birthDate', row, ''),
            },
            {
                title: <FormattedMessage id="status"/>,
                width: 120,
                render: (_: any, row: GridRow) => {
                    // Show Status tag for server-validated rows AND for rows with
                    // client-side errors (during typing) — status is reset to undefined
                    // on each edit, so we synthesise 'invalid' from validationErrors.
                    const hasClientErrors =
                        row.validationErrors && Object.keys(row.validationErrors).length > 0;
                    const effectiveStatus =
                        row.status ?? (hasClientErrors ? ('invalid' as RowStatus) : undefined);
                    if (!effectiveStatus) return null;
                    return (
                        <RowStatusTag
                            status={effectiveStatus}
                            errors={row.validationErrors}
                            duplicateInfo={row.duplicateInfo}
                        />
                    );
                },
            },
            {
                title: '',
                width: 40,
                render: (_: any, row: GridRow) => (
                    <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined/>}
                        disabled={validating}
                        onClick={() => deleteRow(row.key)}
                    />
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, validating, intl]
    );

    return (
        <div>
            {/* form wrapper suppresses browser autofill popups (Chrome ignores
                autoComplete="off" on individual inputs but respects it on <form>) */}
            <form autoComplete="off" onSubmit={e => e.preventDefault()}>
                <Table
                    dataSource={rows}
                    columns={columns}
                    rowKey="key"
                    pagination={false}
                    size="small"
                    scroll={{x: 'max-content'}}
                    rowClassName={row => {
                        if (!row.status) return '';
                        if (row.status === 'saved') return 'import-row-saved';
                        if (row.status === 'invalid' || row.status === 'failed') return 'import-row-error';
                        if (row.status === 'conflict') return 'import-row-conflict';
                        if (row.status === 'duplicate_warning') return 'import-row-warning';
                        return '';
                    }}
                    style={{marginBottom: 8}}
                />
            </form>
            {rows.length < MAX_ROWS && (
                <Button className="rounded-btn" onClick={addEmptyRow} disabled={validating}>
                    <PlusOutlined/>{' '}<FormattedMessage id="import_add_row"/>
                </Button>
            )}
            <div style={{color: '#999', fontSize: 12, marginTop: 4}}>
                <FormattedMessage
                    id="import_row_count"
                    values={{count: rows.length, max: MAX_ROWS}}
                />
            </div>
        </div>
    );
};
